import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete, Category, Search } from '@mui/icons-material';
import { ModuleLayout } from '../../components/Layout/ModuleLayout';
import PendingApprovalsSection from '../../components/common/PendingApprovalsSection';
import apiClient from '../../api/client';
import { moduleService, moduleCategoryService, modulePartService, ModuleCategory } from '../../services';
import { usePermission } from '../../hooks';
import { colors } from '../../theme/theme';

export const ModuleCategoriesPage = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermission();
  const [module, setModule] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [parts, setParts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    part_id: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const filteredCategories = categories.filter((category) => {
    const query = searchQuery.toLowerCase();
    return (
      category.name?.toLowerCase().includes(query) ||
      category.description?.toLowerCase().includes(query) ||
      category.part?.name?.toLowerCase().includes(query)
    );
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 250,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'part',
      headerName: 'Part',
      width: 200,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.value?.name || 'N/A'}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e' }}
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'services_count',
      headerName: 'Services Count',
      width: 150,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          color="primary"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (params) => (
        <Box>
          {can('categories.edit') && (
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ color: colors.primary.main }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          {can('categories.delete') && (
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ color: '#dc2626' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const [moduleRes, categoriesRes, partsRes] = await Promise.all([
        moduleService.getModule(Number(id)),
        moduleCategoryService.getAll(Number(id!)),
        modulePartService.getAllForCounty(), // Fetch all county parts (shared across modules)
      ]);

      if (moduleRes.status === 'success') {
        setModule(moduleRes.data);
      }
      if (categoriesRes.status === 'success') {
        // Handle both paginated and non-paginated responses
        let categoriesData;
        const responseData = categoriesRes.data as any;
        if (responseData?.data && Array.isArray(responseData.data)) {
          // Paginated response
          categoriesData = responseData.data;
        } else if (Array.isArray(categoriesRes.data)) {
          // Direct array
          categoriesData = categoriesRes.data;
        } else {
          categoriesData = [];
        }
        setCategories(categoriesData);
      }
      if (partsRes.status === 'success' && partsRes.data) {
        const partsData = partsRes.data || [];
        setParts(Array.isArray(partsData) ? partsData : []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: any) => {
    if (category) {
      setSelectedCategory(category);
      setFormData({
        name: category.name || '',
        description: category.description || '',
        part_id: category.part_id?.toString() || '',
      });
    } else {
      setSelectedCategory(null);
      setFormData({ name: '', description: '', part_id: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCategory(null);
    setFormData({ name: '', description: '', part_id: '' });
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        part_id: formData.part_id ? parseInt(formData.part_id) : undefined,
      };

      let response;
      if (selectedCategory) {
        response = await moduleCategoryService.update(Number(id!), selectedCategory.id, data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: response.message || 'Update request submitted for approval',
            severity: 'success',
          });
          setTabValue(1);
        }
      } else {
        response = await moduleCategoryService.create(Number(id!), data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: 'Category created successfully',
            severity: 'success',
          });
        }
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Failed to save category:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to save category',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (categoryId: number) => {
    if (!window.confirm('Are you sure you want to submit a delete request for this category?')) return;
    try {
      const response = await moduleCategoryService.delete(Number(id!), categoryId);
      if (response.status === 'success') {
        setSnackbar({
          open: true,
          message: response.message || 'Delete request submitted for approval',
          severity: 'success',
        });
        setTabValue(1);
        fetchData();
      }
    } catch (err: any) {
      console.error('Failed to delete category:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to delete category',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <ModuleLayout module={module} moduleId={id!}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout module={module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Categories
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Organize services into categories
            </Typography>
          </Box>
          {can('categories.create') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Category
            </Button>
          )}
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Categories" />
          <Tab label="Approvals" />
        </Tabs>

        {tabValue === 0 && (
          <>
            <TextField
              fullWidth
              placeholder="Search categories by name, description, or part..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
              sx={{ mb: 3 }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <DataGrid
                  rows={filteredCategories}
                  columns={columns}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[10, 25, 50]}
                  disableRowSelectionOnClick
                  sx={{
                    height: 600,
                    border: 'none',
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-columnHeader:focus': {
                      outline: 'none',
                    },
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: colors.primary[50],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3,
                          }}
                        >
                          <Category sx={{ fontSize: 40, color: colors.primary.main }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          {searchQuery ? 'No Matching Categories' : 'No Categories Found'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {searchQuery ? 'Try adjusting your search terms' : 'Create your first service category'}
                        </Typography>
                        {!searchQuery && can('categories.create') && (
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenDialog()}
                            sx={{
                              background: colors.gradients.primary,
                            }}
                          >
                            Add Category
                          </Button>
                        )}
                      </Box>
                    ),
                  }}
                />
              </CardContent>
            </Card>
          </>
        )}

        {tabValue === 1 && (
          <PendingApprovalsSection
            entityType="category"
            title="Pending Category Approvals"
            onApprovalComplete={fetchData}
          />
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                required
                label="Category Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <FormControl fullWidth required>
                <InputLabel>Part</InputLabel>
                <Select
                  value={formData.part_id}
                  label="Part"
                  onChange={(e) => setFormData({ ...formData, part_id: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Select a part</em>
                  </MenuItem>
                  {parts.map((part) => (
                    <MenuItem key={part.id} value={part.id}>
                      {part.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                background: colors.gradients.primary,
              }}
            >
              {selectedCategory ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ModuleLayout>
  );
};
